import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, FileArrowUp, FilePdf, X, CircleNotch, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import { getJob, applyToJob, uploadFile } from "@/lib/api";
import { BUSINESS } from "@/lib/data";

const field =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:border-navy-600 transition";
const label = "block text-sm font-semibold text-navy-900 mb-1.5";
const RESUME_ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf";
const RESUME_MAX_MB = 15;
const today = () => new Date().toISOString().slice(0, 10);

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-7">
    <h2 className="font-display font-black text-xl text-navy-900 tracking-tight mb-5">{title}</h2>
    {children}
  </div>
);

export default function JobApply() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [resume, setResume] = useState(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const resumeInputRef = useRef(null);

  const [f, setF] = useState({
    name: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "",
    desired_pay: "", start_date: "", availability: "Full-time",
    work_authorized: "Yes", need_sponsorship: "No",
    drivers_license: "", dl_state: "",
    message: "",
    consent: false, signature: "",
  });
  const [employment, setEmployment] = useState([{ employer: "", title: "", start: "", end: "", reason: "" }]);
  const [education, setEducation] = useState([{ school: "", field: "", year: "" }]);
  const [references, setReferences] = useState([{ name: "", relationship: "", contact: "" }]);

  useEffect(() => {
    getJob(id).then(setJob).catch(() => setJob(null)).finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upd = (list, setList, i, k, v) => setList(list.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const handleResume = async (file) => {
    if (!file) return;
    if (file.size > RESUME_MAX_MB * 1024 * 1024) { toast.error(`Resume is too large (max ${RESUME_MAX_MB}MB).`); return; }
    setResumeUploading(true);
    try { setResume(await uploadFile(file)); }
    catch { toast.error("Could not upload that file. Try a PDF or Word doc."); }
    finally { setResumeUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error("Please confirm the information is accurate to submit."); return; }
    if (resumeUploading) { toast.error("Please wait for your resume to finish uploading."); return; }
    setSubmitting(true);
    try {
      await applyToJob(id, {
        name: f.name, email: f.email, phone: f.phone, message: f.message, resume: resume || null,
        details: {
          address: f.address, city: f.city, state: f.state, zip: f.zip,
          desired_pay: f.desired_pay, start_date: f.start_date, availability: f.availability,
          work_authorized: f.work_authorized, need_sponsorship: f.need_sponsorship,
          drivers_license: f.drivers_license, dl_state: f.dl_state,
          employment: employment.filter((x) => x.employer || x.title),
          education: education.filter((x) => x.school || x.field),
          references: references.filter((x) => x.name || x.contact),
          consent: f.consent, signature: f.signature, signed_date: today(),
        },
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error(`Something went wrong. Please call us at ${BUSINESS.phone}.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center pt-32"><p className="text-slate-500">Loading…</p></div>;

  if (!job) return (
    <div className="min-h-[60vh] grid place-items-center pt-32 px-5 text-center" data-testid="apply-job-not-found">
      <div>
        <h1 className="font-display font-black text-3xl text-navy-900">This role isn't available.</h1>
        <button onClick={() => navigate("/careers")} className="inline-flex items-center gap-2 mt-6 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600">
          <ArrowLeft size={16} /> Back to careers
        </button>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-[70vh] grid place-items-center pt-32 px-5 text-center" data-testid="apply-success">
      <div className="max-w-md">
        <CheckCircle size={56} weight="fill" className="text-green-500 mx-auto" />
        <h1 className="font-display font-black text-3xl text-navy-900 mt-4">Application received!</h1>
        <p className="text-slate-600 mt-3">
          Thanks for applying for <strong>{job.title}</strong>, {f.name.split(" ")[0]}. We've emailed you a confirmation and
          will review your application — you'll typically hear back within 3–5 business days.
        </p>
        <Link to="/careers" className="inline-flex items-center gap-2 mt-7 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors">
          <ArrowLeft size={16} /> Back to careers
        </Link>
      </div>
    </div>
  );

  return (
    <div data-testid="job-apply-page">
      <section className="relative bg-navy-900 text-white pt-36 pb-14 md:pt-40 md:pb-16 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-navy-600/30 blur-3xl" />
        <div className="relative max-w-[900px] mx-auto px-5 md:px-10">
          <Link to={`/careers/${id}`} data-testid="apply-back-link" className="inline-flex items-center gap-2 text-sm text-navy-300 hover:text-white mb-5">
            <ArrowLeft size={16} /> Back to job
          </Link>
          <span className="block text-xs font-bold uppercase tracking-[0.2em] text-navy-400">Employment Application</span>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 leading-[1.05]">{job.title}</h1>
        </div>
      </section>

      <form onSubmit={submit} data-testid="full-application-form" className="max-w-[900px] mx-auto px-5 md:px-10 py-14 space-y-6">
        {/* Personal */}
        <Section title="Personal information">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Full name *</label><input required data-testid="fa-name" className={field} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><label className={label}>Email *</label><input required type="email" data-testid="fa-email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={label}>Phone *</label><input required data-testid="fa-phone" className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><label className={label}>Street address *</label><input required data-testid="fa-address" className={field} value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><label className={label}>City *</label><input required data-testid="fa-city" className={field} value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>State *</label><input required data-testid="fa-state" className={field} value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
              <div><label className={label}>ZIP *</label><input required data-testid="fa-zip" className={field} value={f.zip} onChange={(e) => set("zip", e.target.value)} /></div>
            </div>
          </div>
        </Section>

        {/* Position */}
        <Section title="Position details">
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className={label}>Desired pay</label><input data-testid="fa-pay" className={field} placeholder="$22 / hr" value={f.desired_pay} onChange={(e) => set("desired_pay", e.target.value)} /></div>
            <div><label className={label}>Earliest start date</label><input type="date" data-testid="fa-start" className={field} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div><label className={label}>Availability</label>
              <select data-testid="fa-availability" className={field} value={f.availability} onChange={(e) => set("availability", e.target.value)}>
                <option>Full-time</option><option>Part-time</option><option>Either</option>
              </select>
            </div>
          </div>
        </Section>

        {/* Eligibility */}
        <Section title="Work eligibility">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Authorized to work in the U.S.? *</label>
              <select data-testid="fa-authorized" className={field} value={f.work_authorized} onChange={(e) => set("work_authorized", e.target.value)}><option>Yes</option><option>No</option></select>
            </div>
            <div><label className={label}>Do you require sponsorship?</label>
              <select data-testid="fa-sponsorship" className={field} value={f.need_sponsorship} onChange={(e) => set("need_sponsorship", e.target.value)}><option>No</option><option>Yes</option></select>
            </div>
            <div><label className={label}>Driver's license #</label><input data-testid="fa-dl" className={field} value={f.drivers_license} onChange={(e) => set("drivers_license", e.target.value)} /></div>
            <div><label className={label}>License state</label><input data-testid="fa-dl-state" className={field} value={f.dl_state} onChange={(e) => set("dl_state", e.target.value)} /></div>
          </div>
        </Section>

        {/* Employment history */}
        <Section title="Employment history">
          <div className="space-y-4">
            {employment.map((row, i) => (
              <div key={i} data-testid={`fa-emp-${i}`} className="rounded-2xl border border-slate-200 p-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={field} placeholder="Employer" value={row.employer} onChange={(e) => upd(employment, setEmployment, i, "employer", e.target.value)} />
                  <input className={field} placeholder="Job title" value={row.title} onChange={(e) => upd(employment, setEmployment, i, "title", e.target.value)} />
                  <input className={field} placeholder="Start (e.g. Jan 2022)" value={row.start} onChange={(e) => upd(employment, setEmployment, i, "start", e.target.value)} />
                  <input className={field} placeholder="End (or 'Present')" value={row.end} onChange={(e) => upd(employment, setEmployment, i, "end", e.target.value)} />
                </div>
                <input className={`${field} mt-3`} placeholder="Reason for leaving" value={row.reason} onChange={(e) => upd(employment, setEmployment, i, "reason", e.target.value)} />
                {employment.length > 1 && (
                  <button type="button" onClick={() => setEmployment(employment.filter((_, idx) => idx !== i))} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700"><Trash size={15} /> Remove</button>
                )}
              </div>
            ))}
          </div>
          <button type="button" data-testid="fa-add-emp" onClick={() => setEmployment([...employment, { employer: "", title: "", start: "", end: "", reason: "" }])} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><Plus size={16} weight="bold" /> Add another</button>
        </Section>

        {/* Education */}
        <Section title="Education">
          <div className="space-y-4">
            {education.map((row, i) => (
              <div key={i} data-testid={`fa-edu-${i}`} className="grid sm:grid-cols-3 gap-3">
                <input className={field} placeholder="School" value={row.school} onChange={(e) => upd(education, setEducation, i, "school", e.target.value)} />
                <input className={field} placeholder="Degree / field" value={row.field} onChange={(e) => upd(education, setEducation, i, "field", e.target.value)} />
                <div className="flex gap-2">
                  <input className={field} placeholder="Year" value={row.year} onChange={(e) => upd(education, setEducation, i, "year", e.target.value)} />
                  {education.length > 1 && <button type="button" onClick={() => setEducation(education.filter((_, idx) => idx !== i))} aria-label="Remove" className="shrink-0 w-11 grid place-items-center rounded-xl border border-red-300 text-red-600 hover:bg-red-500 hover:text-white"><Trash size={16} /></button>}
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="fa-add-edu" onClick={() => setEducation([...education, { school: "", field: "", year: "" }])} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><Plus size={16} weight="bold" /> Add another</button>
        </Section>

        {/* References */}
        <Section title="References">
          <div className="space-y-4">
            {references.map((row, i) => (
              <div key={i} data-testid={`fa-ref-${i}`} className="grid sm:grid-cols-3 gap-3">
                <input className={field} placeholder="Name" value={row.name} onChange={(e) => upd(references, setReferences, i, "name", e.target.value)} />
                <input className={field} placeholder="Relationship" value={row.relationship} onChange={(e) => upd(references, setReferences, i, "relationship", e.target.value)} />
                <div className="flex gap-2">
                  <input className={field} placeholder="Phone or email" value={row.contact} onChange={(e) => upd(references, setReferences, i, "contact", e.target.value)} />
                  {references.length > 1 && <button type="button" onClick={() => setReferences(references.filter((_, idx) => idx !== i))} aria-label="Remove" className="shrink-0 w-11 grid place-items-center rounded-xl border border-red-300 text-red-600 hover:bg-red-500 hover:text-white"><Trash size={16} /></button>}
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="fa-add-ref" onClick={() => setReferences([...references, { name: "", relationship: "", contact: "" }])} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><Plus size={16} weight="bold" /> Add another</button>
        </Section>

        {/* Resume + message */}
        <Section title="Resume & cover note">
          <label className={label}>Resume (optional)</label>
          <input ref={resumeInputRef} type="file" accept={RESUME_ACCEPT} className="hidden" data-testid="fa-resume-input" onChange={(e) => { handleResume(e.target.files?.[0]); e.target.value = ""; }} />
          {resume ? (
            <div data-testid="fa-resume-file" className="flex items-center gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3">
              <FilePdf size={22} weight="fill" className="text-navy-600 shrink-0" />
              <span className="text-sm text-navy-900 font-medium truncate flex-1">{resume.filename}</span>
              <button type="button" aria-label="Remove resume" data-testid="fa-resume-remove" onClick={() => setResume(null)} className="w-7 h-7 grid place-items-center rounded-full bg-navy-900/80 text-white hover:bg-navy-900"><X size={14} weight="bold" /></button>
            </div>
          ) : (
            <button type="button" data-testid="fa-resume-trigger" onClick={() => resumeInputRef.current?.click()} disabled={resumeUploading} className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-4 font-semibold text-navy-900 hover:border-navy-600 hover:bg-slate-50 transition-colors disabled:opacity-60">
              {resumeUploading ? (<><CircleNotch size={18} className="animate-spin" /> Uploading…</>) : (<><FileArrowUp size={18} weight="bold" /> Attach resume <span className="text-xs font-normal text-slate-400">(PDF/Word · max {RESUME_MAX_MB}MB)</span></>)}
            </button>
          )}
          <div className="mt-4">
            <label className={label}>Anything else you'd like us to know? (optional)</label>
            <textarea rows={4} data-testid="fa-message" className={field} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Experience, certifications, availability notes…" />
          </div>
        </Section>

        {/* Consent */}
        <Section title="Confirm & sign">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" data-testid="fa-consent" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-navy-900 focus:ring-navy-600" />
            <span>I certify that the information provided is true and complete to the best of my knowledge. I understand that false statements may disqualify me from employment.</span>
          </label>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div><label className={label}>Signature (type your full name) *</label><input required data-testid="fa-signature" className={field} value={f.signature} onChange={(e) => set("signature", e.target.value)} placeholder="Jane A. Smith" /></div>
            <div><label className={label}>Date</label><input disabled className={`${field} bg-slate-100`} value={today()} /></div>
          </div>
        </Section>

        <button type="submit" disabled={submitting || resumeUploading} data-testid="fa-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-4 font-bold text-lg hover:bg-navy-600 transition-colors disabled:opacity-60">
          {submitting ? "Submitting…" : "Submit application"} <ArrowUpRight weight="bold" />
        </button>
      </form>
    </div>
  );
}
